import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ColumnDef, FilterClause, FilterOp } from '@/hooks/useReportBuilder';
import { OP_LABELS } from '@/hooks/useReportBuilder';

interface Props {
  columns: ColumnDef[];
  filters: FilterClause[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<FilterClause>) => void;
  onRemove: (id: string) => void;
}

const OPS: FilterOp[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'ilike', 'is'];

export const FilterPanel: React.FC<Props> = ({ columns, filters, onAdd, onUpdate, onRemove }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between px-1">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Filtros</p>
      <Button variant="ghost" size="sm" onClick={onAdd} className="h-6 px-2 text-xs gap-1">
        <Plus className="h-3 w-3" /> Adicionar
      </Button>
    </div>

    {filters.length === 0 && (
      <p className="text-xs text-muted-foreground px-1 py-2">Nenhum filtro aplicado.</p>
    )}

    <div className="space-y-2">
      {filters.map(f => (
        <div key={f.id} className="flex items-center gap-1.5 rounded-lg border border-border/50 bg-muted/30 p-2">
          {/* Column */}
          <Select value={f.column} onValueChange={v => onUpdate(f.id, { column: v })}>
            <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {columns.map(c => (
                <SelectItem key={c.key} value={c.key} className="text-xs">{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Operator */}
          <Select value={f.op} onValueChange={v => onUpdate(f.id, { op: v as FilterOp })}>
            <SelectTrigger className="h-7 text-xs w-20 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPS.map(op => (
                <SelectItem key={op} value={op} className="text-xs">{OP_LABELS[op]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Value — hidden for 'is' (null check) */}
          {f.op !== 'is' && (
            <Input
              value={f.value}
              onChange={e => onUpdate(f.id, { value: e.target.value })}
              className="h-7 text-xs flex-1 min-w-0"
              placeholder="valor..."
            />
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(f.id)}
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  </div>
);
